import { cloneScript, isExtractableScript } from "@/utils/script"
import { transpileCSS } from "@/css/nesting"
import windows from "@/core/windows"
import logger from "@/core/logger"

/**
 * Extract scripts that should be executed in client script iframe context
 */
function extractScripts(doc: Document, domElement: ParentNode) {
  const scripts = Array.from(domElement.querySelectorAll("script")).filter(isExtractableScript)
  scripts.forEach(script => script.remove())

  // recreate module scripts
  const moduleScripts = Array.from(domElement.querySelectorAll<HTMLScriptElement>("script[type='module']"))
  moduleScripts.forEach(script => {
    script.replaceWith(cloneScript(doc, script))
  })

  return scripts
}

/**
 * Insert scripts into the sandboxed iframe document.
 *
 * Scripts are inserted one at a time, waiting for each external script to finish
 * loading before inserting the next one. Dynamically inserted external scripts
 * load asynchronously and do not block, so without this sequencing an inline
 * script that depends on a preceding external one (e.g. code using jQuery) would
 * execute before the dependency is ready and throw a ReferenceError.
 */
async function insertSandboxScripts(scripts: HTMLScriptElement[], containerId: string | undefined): Promise<void> {
  const { nosto } = windows
  for (const script of scripts) {
    // TODO add deduplication based on script.src
    const cloned = cloneScript(nosto.document, script)
    cloned.dataset.containerId = containerId || "unknown"
    if (cloned.text) {
      // wrap in block
      cloned.text = `{\n${cloned.text}\n}`
    }
    if (cloned.src) {
      // external scripts load asynchronously: wait for them so that the
      // next (possibly dependent) script runs only once they are ready
      await new Promise<void>(resolve => {
        cloned.addEventListener("load", () => resolve())
        cloned.addEventListener("error", () => {
          logger.warn(`Error loading ${cloned.src}`)
          resolve()
        })
        nosto.document.head.appendChild(cloned)
      })
    } else {
      // inline scripts execute synchronously on insertion
      nosto.document.head.appendChild(cloned)
    }
  }
}

const ep = Element.prototype

const operationMapping = {
  REPLACE: ep.replaceWith,
  APPEND: ep.after,
  PREPEND: ep.before,
  INSERT_INTO: ep.append,
  INSERT_AFTER_BEGIN: ep.prepend
} as const

type DomOperation = typeof ep.replaceWith
type InsertMode = keyof typeof operationMapping

function invalidInjection(mode: string) {
  throw new Error(`Invalid injection mode ${mode}`)
}

function domOperation(mode: InsertMode): DomOperation {
  return operationMapping[mode] || invalidInjection(mode)
}

function cleanScripts(doc: Document, elements: HTMLElement[]) {
  // create document fragment
  const df = doc.createDocumentFragment()
  df.append(...elements)
  // to extract scripts in one go
  const scripts = extractScripts(doc, df)
  const children = df.childNodes
  // synchronize dom array (remove scripts)
  elements.length = children.length
  children.forEach((e, i) => (elements[i] = e))
  return scripts
}

export default function createSelector(doc: Document) {
  function normalize(content: string | HTMLElement) {
    if (typeof content === "string") {
      const div = doc.createElement("div")
      div.innerHTML = content.trim()
      return Array.from(div.childNodes)
    }
    return [content]
  }

  function html(container: HTMLElement, htmlText: string) {
    const elements = normalize(htmlText)
    transpileCSS(...elements)
    container.replaceChildren(...elements)
    insertSandboxScripts(extractScripts(doc, container), container.id)
  }

  function performOperation(mode: InsertMode, target: HTMLElement, content: string | HTMLElement, divId?: string) {
    return performDomOperation(mode, target, normalize(content), divId)
  }

  function performDomOperation(mode: InsertMode, target: HTMLElement, elements: HTMLElement[], divId?: string) {
    const scripts = cleanScripts(doc, elements)
    transpileCSS(...elements)
    domOperation(mode).apply(target, elements)
    insertSandboxScripts(scripts, divId)
  }

  return {
    html,
    performOperation,
    performDomOperation
  }
}

export type Selector = ReturnType<typeof createSelector>

/**
 * to be used with template literals to perform css escaping for user-provided parts
 * css`#${divId}`
 */
export function cssEscape(arr: TemplateStringsArray, ...items: unknown[]) {
  return items.map((e, i) => arr[i] + CSS.escape(String(e))).join("") + arr[arr.length - 1]
}
